import { ADMIN, TEACHER } from './constants'

/**
 * Validates if any sites contain the specified program key.
 * Excludes family-only sites from validation (sites that only have family claims).
 *
 * @param {Array} sites - Array of site objects
 * @param {string} programKey - The program key to check for (e.g., 'highSchool', 'mspDigital', 'k5SelDigital')
 * @returns {boolean} - True if at least one valid site has the program key, false otherwise
 */
export function hasSitesWithProgram(sites, programKey) {
  if (!sites || !Array.isArray(sites) || sites.length === 0) {
    return false
  }

  if (!programKey) {
    return false
  }

  // Check if any site has the required program and is not family-only
  return sites.some(site => {
    // Site must have the required program
    if (!site?.programs?.[programKey]) {
      return false
    }

    // Site must have ADMIN or TEACHER claims (not be family-only)
    const claims = site.claims || []
    return claims.includes(ADMIN) || claims.includes(TEACHER)
  })
}

/**
 * Validates if userContextManager has sites with the specified program key.
 * Returns an object with validation result and details.
 *
 * @param {Object} userContextManager - The userContextManager state object
 * @param {string} programKey - The program key to validate
 * @returns {Object} - Validation result object with isValid and reason properties
 */
export function validateUserContextForProgram(userContextManager, programKey) {
  // Check for null/undefined userContextManager
  if (!userContextManager) {
    return {
      isValid: false,
      reason: 'userContextManager is null or undefined',
    }
  }

  // Check if userContextManager has sites
  if (!userContextManager.sites) {
    return {
      isValid: false,
      reason: 'userContextManager has no sites property',
    }
  }

  // Check if any sites contain the program key
  if (!hasSitesWithProgram(userContextManager.sites, programKey)) {
    return {
      isValid: false,
      reason: `no sites contain the program key: ${programKey}`,
    }
  }

  return {
    isValid: true,
    reason: null,
  }
}
